import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Plus, Trash2, X, ChevronDown } from 'lucide-react';

// ─── Colour system ────────────────────────────────────────────────────────────
const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8',
  orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF',
  cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3',
  skyBlue:'#BCD9E5', skyBlueLight:'#EAF4F8', skyText:'#3A8FA3',
  white:'#FFFFFF', red:'#DC3545', redLight:'#FFF0F0',
  amber:'#E07B00', amberLight:'#FFF3E0',
  pink:'#F2B8CB', pinkLight:'#FDF0F5', pinkActive:'#C4607A',
};

const FLOAT = 300;

const DENOMS = [
  { key: 'count_100', label: '€100', value: 100,  type: 'note' },
  { key: 'count_50',  label: '€50',  value: 50,   type: 'note' },
  { key: 'count_20',  label: '€20',  value: 20,   type: 'note' },
  { key: 'count_10',  label: '€10',  value: 10,   type: 'note' },
  { key: 'count_5',   label: '€5',   value: 5,    type: 'note' },
  { key: 'count_2',   label: '€2',   value: 2,    type: 'coin' },
  { key: 'count_1',   label: '€1',   value: 1,    type: 'coin' },
  { key: 'count_050', label: '50c',  value: 0.5,  type: 'coin' },
  { key: 'count_020', label: '20c',  value: 0.2,  type: 'coin' },
  { key: 'count_010', label: '10c',  value: 0.1,  type: 'coin' },
];

// ─── Date helpers (local time, 2-hour rollback for late-night submissions) ────
// Anything submitted before 02:00 is attributed to the previous trading day
function todayStr() {
  const d = new Date();
  d.setHours(d.getHours() - 2);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function localDateStr(date) {
  // Local date string without UTC bleed — no 2hr offset, used for display/nav
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatDateTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
    ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ─── Variance chip ────────────────────────────────────────────────────────────
function VarianceChip({ variance, size = 'md' }) {
  if (variance === null || variance === undefined) return null;
  const abs = Math.abs(variance);
  let color, bg;
  if (abs <= 5)       { color = C.green; bg = C.greenLight; }
  else if (abs <= 10) { color = C.amber; bg = C.amberLight; }
  else                { color = C.red;   bg = C.redLight;   }
  const label = `${variance >= 0 ? '+' : '-'}€${abs.toFixed(2)}`;
  const fs = size === 'lg' ? '1.1rem' : size === 'sm' ? '0.7rem' : '0.82rem';
  return (
    <span style={{ fontSize: fs, fontWeight: 700, color, background: bg,
      padding: size === 'sm' ? '0.1rem 0.4rem' : '0.2rem 0.6rem',
      borderRadius: '999px', display: 'inline-block' }}>
      {label}
    </span>
  );
}

// ─── Drop reminder modal ──────────────────────────────────────────────────────
function DropReminderModal({ dropAmount, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(47,58,66,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1.25rem',
    }}>
      <div style={{
        background: C.green, borderRadius: '22px',
        width: '100%', maxWidth: '380px',
        padding: '2rem 1.75rem',
        boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '3rem', lineHeight: 1, marginBottom: '0.5rem' }}>🍋</div>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.35rem', fontWeight: 800, color: C.white }}>
          Cash up complete!
        </h2>
        <div style={{
          background: 'rgba(255,255,255,0.22)', borderRadius: '14px',
          padding: '0.85rem 1rem', margin: '0.85rem 0 1.1rem',
        }}>
          <p style={{ margin: '0 0 0.1rem', fontSize: '0.7rem', color: `${C.white}bb`,
            fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Drop into the safe
          </p>
          <p style={{ margin: 0, fontSize: '2.2rem', fontWeight: 900, color: C.white, letterSpacing: '-0.01em' }}>
            €{dropAmount.toFixed(2)}
          </p>
          <p style={{ margin: '0.15rem 0 0', fontSize: '0.73rem', color: `${C.white}aa` }}>
            cash counted + receipts − €{FLOAT} float
          </p>
        </div>
        <div style={{
          background: 'rgba(255,255,255,0.15)', borderRadius: '12px',
          padding: '0.85rem 1rem', marginBottom: '1.25rem', textAlign: 'left',
        }}>
          <p style={{ margin: 0, fontSize: '0.88rem', color: C.white, fontWeight: 600, lineHeight: 1.55 }}>
            Please ensure today's date is clearly showing on the outside of the bag, with cash neatly inside — do not fold cash.
          </p>
        </div>
        <button onClick={onClose} style={{
          width: '100%', padding: '0.78rem',
          background: C.white, color: C.green,
          border: 'none', borderRadius: '12px',
          fontSize: '1rem', fontWeight: 800,
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          All sorted ✓
        </button>
      </div>
    </div>
  );
}

// ─── Denomination stepper ─────────────────────────────────────────────────────
function DenomStepper({ denom, value, onChange }) {
  const count    = parseInt(value) || 0;
  const subtotal = count * denom.value;

  function decrement() { onChange(String(Math.max(0, count - 1))); }
  function increment() { onChange(String(count + 1)); }

  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      gap: '0.5rem', marginBottom: '0.55rem',
    }}>
      {/* Label */}
      <span style={{
        width: '38px', fontSize: '0.85rem', fontWeight: 700,
        color: count > 0 ? C.charcoal : C.mid, flexShrink: 0,
      }}>
        {denom.label}
      </span>

      {/* Stepper */}
      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <button
          onClick={decrement}
          disabled={count <= 0}
          style={{
            width: '44px', height: '44px',
            borderRadius: '10px 0 0 10px',
            border: `2px solid ${count > 0 ? C.navy : C.border}`,
            borderRight: 'none',
            background: count > 0 ? C.navy : C.cream,
            color: count > 0 ? C.white : C.mid,
            fontSize: '1.4rem', fontWeight: 700,
            cursor: count > 0 ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, userSelect: 'none',
            WebkitTapHighlightColor: 'transparent',
            transition: 'all 0.12s',
            touchAction: 'manipulation',
          }}>
          −
        </button>
        <input
          type="number" min="0" step="1"
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{
            width: '52px', height: '44px',
            border: `2px solid ${count > 0 ? C.navy : C.border}`,
            borderLeft: 'none', borderRight: 'none',
            textAlign: 'center',
            fontSize: '1.05rem', fontWeight: 800,
            color: count > 0 ? C.navy : C.mid,
            background: count > 0 ? C.white : C.cream,
            outline: 'none',
            MozAppearance: 'textfield',
          }}
        />
        <button
          onClick={increment}
          style={{
            width: '44px', height: '44px',
            borderRadius: '0 10px 10px 0',
            border: `2px solid ${C.navy}`,
            borderLeft: 'none',
            background: C.navy, color: C.white,
            fontSize: '1.4rem', fontWeight: 700,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, userSelect: 'none',
            WebkitTapHighlightColor: 'transparent',
            transition: 'all 0.12s',
            touchAction: 'manipulation',
          }}>
          +
        </button>
      </div>

      {/* Subtotal */}
      <span style={{
        fontSize: '0.85rem', marginLeft: 'auto',
        color: subtotal > 0 ? C.charcoal : C.border,
        fontWeight: subtotal > 0 ? 700 : 400,
        minWidth: '48px', textAlign: 'right',
      }}>
        {subtotal > 0 ? `€${subtotal % 1 === 0 ? subtotal.toFixed(0) : subtotal.toFixed(2)}` : '—'}
      </span>
    </div>
  );
}

// ─── CASH UP SECTION (staff + supervisor — inside Checklist.js) ───────────────
export function CashUpSection({ venue, currentUser }) {
  const today = todayStr();

  const [submitterName, setSubmitterName] = useState('');
  const [counts,        setCounts]        = useState(() => Object.fromEntries(DENOMS.map(d => [d.key, ''])));
  const [tebiTotal,     setTebiTotal]     = useState('');
  const [receipts,      setReceipts]      = useState([]);
  const [notes,         setNotes]         = useState('');
  const [submitting,    setSubmitting]    = useState(false);
  const [submitted,     setSubmitted]     = useState(null);
  const [showDrop,      setShowDrop]      = useState(false);
  const [existing,      setExisting]      = useState(null);
  const [amending,      setAmending]      = useState(false);
  const [yesterdayVariance, setYesterdayVariance] = useState(null);

  useEffect(() => {
    if (!venue) return;
    supabase.from('cashup_log').select('*')
      .eq('venue', venue).eq('date', today).maybeSingle()
      .then(({ data }) => { if (data) { setExisting(data); setSubmitted(data); } });

    // Yesterday uses same 2hr anchor so "yesterday" is consistent with "today"
    const anchor = new Date();
    anchor.setHours(anchor.getHours() - 2);
    const yesterday = new Date(anchor);
    yesterday.setDate(yesterday.getDate() - 1);
    const yStr = localDateStr(yesterday);

    supabase.from('cashup_log').select('variance')
      .eq('venue', venue).eq('date', yStr).maybeSingle()
      .then(({ data }) => { if (data) setYesterdayVariance(data.variance); });
  }, [venue, today]);

  const cashTotal     = DENOMS.reduce((s, d) => s + (parseFloat(counts[d.key]) || 0) * d.value, 0);
  const receiptsTotal = receipts.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const tebi          = parseFloat(tebiTotal) || 0;
  const dropAmount    = Math.max(0, cashTotal - FLOAT + receiptsTotal);
  const variance      = (cashTotal > 0 || tebi > 0) ? dropAmount - tebi : null;

  function addReceipt() { setReceipts(p => [...p, { id: Date.now(), amount: '', description: '' }]); }
  function updateReceipt(id, f, v) { setReceipts(p => p.map(r => r.id === id ? { ...r, [f]: v } : r)); }
  function removeReceipt(id) { setReceipts(p => p.filter(r => r.id !== id)); }

  function populateFromExisting(rec) {
    const newCounts = {};
    DENOMS.forEach(d => { newCounts[d.key] = rec[d.key] != null ? String(rec[d.key]) : ''; });
    setCounts(newCounts);
    setTebiTotal(rec.tebi_total != null ? String(rec.tebi_total) : '');
    setReceipts((rec.receipts || []).map((r, i) => ({ ...r, id: i })));
    setNotes(rec.notes || '');
    setSubmitterName(rec.submitted_by || '');
  }

  async function handleSubmit() {
    if (!tebiTotal || cashTotal === 0 || !submitterName.trim()) return;
    if (existing?.confirmed) return;
    setSubmitting(true);

    const expectedCash = tebi - FLOAT + receiptsTotal;
    const payload = {
      venue, date: today,
      submitted_by: submitterName.trim(),
      submitted_at: new Date().toISOString(),
      tebi_total: tebi,
      ...Object.fromEntries(DENOMS.map(d => [d.key, parseInt(counts[d.key]) || 0])),
      cash_total: Math.round(cashTotal * 100) / 100,
      float_amount: FLOAT,
      receipts: receipts.filter(r => r.amount),
      receipts_total: Math.round(receiptsTotal * 100) / 100,
      expected_cash: Math.round(expectedCash * 100) / 100,
      variance: Math.round(variance * 100) / 100,
      notes: notes.trim() || null,
      bag_confirmed: true,
    };

    let data;
    if (existing && amending) {
      const { data: d } = await supabase.from('cashup_log')
        .update({ ...payload, amended: true, amended_at: new Date().toISOString(), amended_by: submitterName.trim() })
        .eq('id', existing.id).select().single();
      data = d;
    } else {
      const { data: d } = await supabase.from('cashup_log').insert(payload).select().single();
      data = d;
    }

    setSubmitting(false);
    if (data) {
      setSubmitted(data); setExisting(data); setAmending(false); setShowDrop(true);
    }
  }

  // ── Already submitted view ────────────────────────────────────────────────
  if (submitted && !amending) {
    const drop = Math.max(0, (submitted.cash_total || 0) - FLOAT + (submitted.receipts_total || 0));
    return (
      <div style={{ background: C.greenLight, borderRadius: '14px', padding: '1.1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.3rem' }}>🍋</span>
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: C.charcoal }}>Cash Up Complete</p>
              <p style={{ margin: 0, fontSize: '0.72rem', color: C.mid }}>
                {submitted.submitted_by || 'unknown'} · {formatDateTime(submitted.submitted_at)}
                {submitted.amended   && <span style={{ color: C.amber, marginLeft: '0.4rem', fontWeight: 600 }}>· Amended</span>}
                {submitted.confirmed && <span style={{ color: C.green, marginLeft: '0.4rem', fontWeight: 600 }}>· Confirmed ✓</span>}
              </p>
            </div>
          </div>
          {submitted.confirmed ? (
            <span style={{ fontSize: '0.72rem', color: C.green, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              🔒 Confirmed
            </span>
          ) : (
            <button onClick={() => { setAmending(true); populateFromExisting(submitted); }}
              style={{ padding: '0.28rem 0.7rem', border: `1px solid ${C.border}`, borderRadius: '7px',
                background: C.white, color: C.mid, fontSize: '0.73rem', cursor: 'pointer' }}>
              Amend
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {[
            { label: 'Cash Counted', value: `€${submitted.cash_total?.toFixed(2)}` },
            { label: 'Drop Amount',  value: `€${drop.toFixed(2)}`, highlight: true },
            { label: 'Variance',     isVariance: true, variance: submitted.variance },
          ].map(({ label, value, highlight, isVariance, variance: v }) => (
            <div key={label} style={{ padding: '0.6rem 0.7rem',
              background: highlight ? C.green : C.white,
              borderRadius: '10px', textAlign: 'center' }}>
              <p style={{ margin: '0 0 0.15rem', fontSize: '0.62rem',
                color: highlight ? `${C.white}bb` : C.mid,
                textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}>{label}</p>
              {isVariance
                ? <VarianceChip variance={v} size="sm" />
                : <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: highlight ? C.white : C.charcoal }}>{value}</p>}
            </div>
          ))}
        </div>

        {submitted.receipts?.length > 0 && (
          <div style={{ padding: '0.6rem 0.8rem', background: C.white, borderRadius: '9px', marginBottom: '0.6rem' }}>
            <p style={{ margin: '0 0 0.35rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Receipts</p>
            {submitted.receipts.map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: C.charcoal, marginBottom: '0.15rem' }}>
                <span>{r.description || '—'}</span>
                <span style={{ fontWeight: 600 }}>€{parseFloat(r.amount).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        {submitted.notes && (
          <div style={{ padding: '0.5rem 0.75rem', background: C.white, borderRadius: '9px', fontSize: '0.82rem', color: C.charcoal }}>
            <span style={{ fontWeight: 600, color: C.mid }}>Note: </span>{submitted.notes}
          </div>
        )}

        {yesterdayVariance !== null && Math.abs(yesterdayVariance) > 5 && (
          <div style={{ marginTop: '0.65rem', padding: '0.5rem 0.8rem', background: C.amberLight, borderRadius: '8px', fontSize: '0.78rem', color: C.amber }}>
            ⚠ Yesterday's variance: <strong>{yesterdayVariance >= 0 ? '+' : ''}€{yesterdayVariance.toFixed(2)}</strong>
          </div>
        )}

        {showDrop && (
          <DropReminderModal
            dropAmount={Math.max(0, drop)}
            onClose={() => setShowDrop(false)}
          />
        )}
      </div>
    );
  }

  // ── Input form ─────────────────────────────────────────────────────────────
  const canSubmit = !!tebiTotal && cashTotal > 0 && !!submitterName.trim();

  return (
    <div style={{ background: C.greenLight, borderRadius: '14px', padding: '1.1rem' }}>
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
        <div>
          <h3 style={{ margin: '0 0 0.1rem', fontSize: '0.95rem', fontWeight: 700, color: C.charcoal }}>
            Cash Up 💰
            {amending && <span style={{ fontSize: '0.72rem', color: C.amber, fontWeight: 600, marginLeft: '0.5rem' }}>— Amending</span>}
          </h3>
          <p style={{ margin: 0, fontSize: '0.72rem', color: C.mid }}>{venue} · Float: €{FLOAT}</p>
        </div>
        {amending && (
          <button onClick={() => setAmending(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={15} /></button>
        )}
      </div>

      {yesterdayVariance !== null && Math.abs(yesterdayVariance) > 5 && (
        <div style={{ padding: '0.4rem 0.7rem', background: C.amberLight, border: `1px solid ${C.orange}44`,
          borderRadius: '7px', marginBottom: '0.7rem', fontSize: '0.75rem', color: C.amber }}>
          ⚠ Yesterday: <strong>{yesterdayVariance >= 0 ? '+' : ''}€{yesterdayVariance.toFixed(2)}</strong> variance
        </div>
      )}

      {/* Name / initials field */}
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{
          display: 'block', fontSize: '0.72rem', fontWeight: 800,
          color: C.navy, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.38rem',
        }}>
          Your name / initials <span style={{ color: C.red }}>*</span>
        </label>
        <input
          type="text"
          value={submitterName}
          onChange={e => setSubmitterName(e.target.value)}
          placeholder="e.g. Sarah / SB"
          maxLength={40}
          style={{
            width: '100%', padding: '0.5rem 0.65rem',
            border: `2px solid ${submitterName.trim() ? C.navy : C.border}`,
            borderRadius: '10px', fontSize: '0.95rem', fontWeight: 600,
            color: C.charcoal, background: C.white, outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s',
          }}
        />
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* LEFT — denomination steppers */}
        <div style={{ flex: '1 1 200px', background: C.white, borderRadius: '12px',
          padding: '0.85rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <p style={{ margin: '0 0 0.7rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid,
            textTransform: 'uppercase', letterSpacing: '0.04em' }}>Cash Count</p>

          {DENOMS.map((d, idx) => {
            const isFirstCoin = d.type === 'coin' && DENOMS[idx - 1]?.type === 'note';
            return (
              <div key={d.key}>
                {isFirstCoin && <div style={{ borderTop: `1.5px dashed ${C.border}`, margin: '0.6rem 0' }} />}
                <DenomStepper
                  denom={d}
                  value={counts[d.key]}
                  onChange={v => setCounts(prev => ({ ...prev, [d.key]: v }))}
                />
              </div>
            );
          })}

          <div style={{ borderTop: `1.5px solid ${C.border}`, paddingTop: '0.55rem', marginTop: '0.3rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: C.mid, fontWeight: 600 }}>Total</span>
            <span style={{ fontSize: '1.2rem', fontWeight: 900, color: C.charcoal }}>€{cashTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* RIGHT — Tebi + receipts + summary */}
        <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>

          {/* Tebi Cash Total */}
          <div style={{ background: C.white, borderRadius: '12px', padding: '0.85rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 800, color: C.navy,
              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.38rem' }}>
              Tebi Cash Payments
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.38rem' }}>
              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: C.mid }}>€</span>
              <input
                type="number" step="0.01" min="0"
                value={tebiTotal}
                onChange={e => setTebiTotal(e.target.value)}
                placeholder="0.00"
                style={{ flex: 1, padding: '0.42rem 0.55rem',
                  border: `2px solid ${tebiTotal ? C.navy : C.border}`,
                  borderRadius: '9px', fontSize: '1rem', fontWeight: 700,
                  color: C.charcoal, outline: 'none', background: C.cream }}
              />
            </div>
          </div>

          {/* Receipts */}
          <div style={{ background: C.white, borderRadius: '12px', padding: '0.85rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: receipts.length > 0 ? '0.5rem' : 0 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 700, color: C.mid,
                textTransform: 'uppercase', letterSpacing: '0.04em' }}>Receipts</label>
              <button onClick={addReceipt}
                style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', padding: '0.18rem 0.5rem',
                  border: `1px solid ${C.border}`, borderRadius: '6px', background: C.cream,
                  color: C.charcoal, fontSize: '0.7rem', cursor: 'pointer' }}>
                <Plus size={10} />Add
              </button>
            </div>
            {receipts.length === 0 && <p style={{ margin: 0, fontSize: '0.75rem', color: C.mid }}>None today</p>}
            {receipts.map(r => (
              <div key={r.id} style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', marginBottom: '0.38rem' }}>
                <span style={{ fontSize: '0.82rem', color: C.mid }}>€</span>
                <input type="number" step="0.01" min="0" value={r.amount}
                  onChange={e => updateReceipt(r.id, 'amount', e.target.value)}
                  placeholder="0.00"
                  style={{ width: '65px', padding: '0.28rem 0.32rem', border: `1px solid ${C.border}`,
                    borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700,
                    textAlign: 'center', color: C.charcoal }} />
                <input type="text" value={r.description}
                  onChange={e => updateReceipt(r.id, 'description', e.target.value)}
                  placeholder="What for?"
                  style={{ flex: 1, padding: '0.28rem 0.42rem', border: `1px solid ${C.border}`,
                    borderRadius: '6px', fontSize: '0.78rem', color: C.charcoal }} />
                <button onClick={() => removeReceipt(r.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: '0.1rem', flexShrink: 0 }}>
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
            {receipts.length > 0 && receiptsTotal > 0 && (
              <div style={{ fontSize: '0.75rem', color: C.mid, textAlign: 'right', marginTop: '0.15rem' }}>
                Total: <strong style={{ color: C.charcoal }}>€{receiptsTotal.toFixed(2)}</strong>
              </div>
            )}
          </div>

          {/* Summary */}
          {(cashTotal > 0 || tebi > 0) && (
            <div style={{ background: C.white, borderRadius: '12px', padding: '0.85rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <p style={{ margin: '0 0 0.55rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid,
                textTransform: 'uppercase', letterSpacing: '0.04em' }}>Summary</p>
              {[
                { label: 'Cash counted',  value: `€${cashTotal.toFixed(2)}` },
                ...(receiptsTotal > 0 ? [{ label: 'Add receipts', value: `+€${receiptsTotal.toFixed(2)}` }] : []),
                { label: 'Less float',    value: `-€${FLOAT.toFixed(2)}` },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between',
                  fontSize: '0.8rem', color: C.mid, marginBottom: '0.25rem' }}>
                  <span>{label}</span><span>{value}</span>
                </div>
              ))}
              <div style={{ background: C.green, borderRadius: '9px', padding: '0.5rem 0.7rem',
                margin: '0.45rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: C.white }}>= Drop amount</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 900, color: C.white }}>€{dropAmount.toFixed(2)}</span>
              </div>
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '0.45rem', marginTop: '0.15rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  fontSize: '0.78rem', color: C.mid, marginBottom: '0.25rem' }}>
                  <span>Tebi total</span>
                  <span>{tebi > 0 ? `€${tebi.toFixed(2)}` : '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: C.charcoal }}>Variance</span>
                  {variance !== null
                    ? <VarianceChip variance={variance} size="sm" />
                    : <span style={{ fontSize: '0.78rem', color: C.border }}>—</span>}
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <textarea
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Notes for the manager... (optional)"
            rows={2}
            style={{ width: '100%', padding: '0.4rem 0.6rem',
              border: `1px solid ${C.border}`, borderRadius: '9px', fontSize: '0.8rem',
              resize: 'vertical', boxSizing: 'border-box', color: C.charcoal, background: C.white }}
          />

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            style={{
              width: '100%', padding: '0.7rem',
              background: canSubmit ? C.green : C.border,
              color: C.white, border: 'none', borderRadius: '12px',
              fontSize: '0.93rem', fontWeight: 800,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              boxShadow: canSubmit ? `0 4px 14px ${C.green}55` : 'none',
              transition: 'all 0.15s',
            }}>
            {submitting ? 'Saving…' : amending ? 'Save Amendment 🍋' : 'Submit Cash Up 🍋'}
          </button>

          {!canSubmit && (
            <p style={{ margin: '0.15rem 0 0', fontSize: '0.71rem', color: C.mid, textAlign: 'center' }}>
              {!submitterName.trim() ? 'Enter your name to continue'
                : !tebiTotal ? 'Enter Tebi Cash Total to continue'
                : 'Count your cash to continue'}
            </p>
          )}
        </div>
      </div>

      {showDrop && submitted && (
        <DropReminderModal
          dropAmount={Math.max(0, (submitted.cash_total || 0) - FLOAT + (submitted.receipts_total || 0))}
          onClose={() => setShowDrop(false)}
        />
      )}
    </div>
  );
}

// ─── CASH UP TAB (manager / admin reports) ────────────────────────────────────
export function CashUpTab({ currentUser, isAdmin: isAdminProp }) {
  const isAdmin = isAdminProp || currentUser?.role?.toLowerCase() === 'admin';

  const VENUES   = ['Box Jordaan', 'Box Plantage'];
  const VENUE_ID = {
    'Box Jordaan':  { bg:'#EDF3FC', text:'#2563EB', border:'#BAD4F5', short:'Jordaan'  },
    'Box Plantage': { bg:'#C8E6D8', text:'#2D7A65', border:'#7EA594', short:'Plantage' },
  };

  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
    d.setHours(0,0,0,0);
    return d;
  }
  function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
  function toStr(date) {
    return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
  }
  function wkLabel(start, end) {
    const opts = { day:'numeric', month:'short' };
    return `${start.toLocaleDateString('en-GB', opts)} – ${end.toLocaleDateString('en-GB', {...opts, year:'numeric'})}`;
  }

  const [weekStart,  setWeekStart]  = useState(() => getMonday(new Date()));
  const [entries,    setEntries]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [openVenue,  setOpenVenue]  = useState(null);
  const [confirming,       setConfirming]       = useState(null);
  const [amendingEntry,    setAmendingEntry]    = useState(null);
  const [amendingReceipts, setAmendingReceipts] = useState(null);
  const [amendSaving,      setAmendSaving]      = useState(false);

  const weekDays   = Array.from({ length:7 }, (_,i) => addDays(weekStart, i));
  const weekEnd    = weekDays[6];
  const fromStr    = toStr(weekStart);
  const toStrEnd   = toStr(weekEnd);
  const isThisWeek = toStr(getMonday(new Date())) === toStr(weekStart);
  const prevMonday = getMonday(addDays(weekStart, -1));
  const nextMonday = getMonday(addDays(weekEnd,    1));
  const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const today      = toStr(new Date());

  useEffect(() => {
    setLoading(true);
    supabase.from('cashup_log').select('*')
      .gte('date', fromStr).lte('date', toStrEnd)
      .order('date', { ascending: true })
      .then(({ data }) => { setEntries(data||[]); setLoading(false); });
  }, [fromStr, toStrEnd]);

  function getEntry(venue, dateStr) {
    return entries.find(e => e.venue === venue && e.date === dateStr) || null;
  }

  function venueWeekStats(venue) {
    const rows = entries.filter(e => e.venue === venue);
    if (!rows.length) return null;
    const totalDrop     = rows.reduce((s,e) => s + Math.max(0,(e.cash_total||0) - FLOAT + (e.receipts_total||0)), 0);
    const totalTebi     = rows.reduce((s,e) => s + (e.tebi_total||0), 0);
    const totalVariance = rows.reduce((s,e) => s + (e.variance||0), 0);
    const avgVariance   = totalVariance / rows.length;
    const daysSubmitted = rows.length;
    const daysExpected  = weekDays.filter(d => toStr(d) <= today).length;
    return { totalDrop, totalTebi, avgVariance, daysSubmitted, daysExpected };
  }

  async function handleConfirm(entry) {
    setConfirming(entry.id);
    const update = { confirmed:true, confirmed_by: currentUser?.name||'admin', confirmed_at: new Date().toISOString() };
    await supabase.from('cashup_log').update(update).eq('id', entry.id);
    setEntries(prev => prev.map(e => e.id===entry.id ? {...e,...update} : e));
    setConfirming(null);
  }

  async function handleUnconfirm(entry) {
    const ok = window.confirm(`Reopen this cash drop confirmation for ${entry.venue} on ${entry.date}?\n\nThis should only be done to correct an error.`);
    if (!ok) return;
    setConfirming(entry.id);
    const update = { confirmed:false, confirmed_by:null, confirmed_at:null };
    await supabase.from('cashup_log').update(update).eq('id', entry.id);
    setEntries(prev => prev.map(e => e.id===entry.id ? {...e,...update} : e));
    setConfirming(null);
  }

  async function handleAmendDrop(entry, newCashTotal) {
    setAmendSaving(true);
    const newDrop     = Math.max(0, newCashTotal - FLOAT + (entry.receipts_total || 0));
    const newVariance = newDrop - (entry.tebi_total || 0);
    const update = {
      cash_total:   Math.round(newCashTotal * 100) / 100,
      variance:     Math.round(newVariance  * 100) / 100,
      amended:      true,
      amended_at:   new Date().toISOString(),
      amended_by:   currentUser?.name || 'admin',
      confirmed:    false,
      confirmed_by: null,
      confirmed_at: null,
    };
    await supabase.from('cashup_log').update(update).eq('id', entry.id);
    setEntries(prev => prev.map(e => e.id===entry.id ? {...e,...update} : e));
    setAmendSaving(false);
    setAmendingEntry(null);
  }

  async function handleAmendReceipts(entry, newReceipts) {
    setAmendSaving(true);
    const newReceiptsTotal = newReceipts.reduce((s,r) => s + (parseFloat(r.amount)||0), 0);
    const newDrop     = Math.max(0, (entry.cash_total||0) - FLOAT + newReceiptsTotal);
    const newVariance = newDrop - (entry.tebi_total || 0);
    const clean = newReceipts.filter(r => r.amount).map(({ id, ...r }) => r); // strip local id
    const update = {
      receipts:       clean,
      receipts_total: Math.round(newReceiptsTotal * 100) / 100,
      variance:       Math.round(newVariance * 100) / 100,
      amended:        true,
      amended_at:     new Date().toISOString(),
      amended_by:     currentUser?.name || 'admin',
      confirmed:      false,
      confirmed_by:   null,
      confirmed_at:   null,
    };
    await supabase.from('cashup_log').update(update).eq('id', entry.id);
    setEntries(prev => prev.map(e => e.id===entry.id ? {...e,...update} : e));
    setAmendSaving(false);
    setAmendingReceipts(null);
  }

  function varChip(variance, size='sm') {
    if (variance === null || variance === undefined) return null;
    const abs   = Math.abs(variance);
    const color = abs<=5 ? C.green : abs<=10 ? C.amber : C.red;
    const bg    = abs<=5 ? C.greenLight : abs<=10 ? C.amberLight : C.redLight;
    const fs    = size==='lg' ? '1rem' : '0.72rem';
    return (
      <span style={{ fontSize:fs, fontWeight:700, color, background:bg,
        padding: size==='lg' ? '0.25rem 0.7rem' : '0.1rem 0.45rem',
        borderRadius:'999px', display:'inline-block' }}>
        {variance>=0?'+':'-'}€{abs.toFixed(2)}
      </span>
    );
  }


  // ── Desktop day cell ─────────────────────────────────────────────────────────
  // Fixed row heights — every cell identical regardless of data
  const ROW_H = { submitter:'1.5rem', receipts:'1.4rem', drop:'2.4rem', tebi:'1.5rem', variance:'1.7rem', amended:'1.2rem', action:'2.1rem' };

  function DayCell({ venue, day }) {
    const dateStr   = toStr(day);
    const entry     = getEntry(venue, dateStr);
    const isToday   = dateStr === today;
    const isFuture  = dateStr > today;
    const count     = entry ? Math.max(0, (entry.cash_total||0) - FLOAT + (entry.receipts_total||0)) : null;
    const confirmed = entry?.confirmed;

    let cellBg = C.white;
    if (confirmed)    cellBg = '#EEF8F3';
    else if (isToday) cellBg = '#F7FAFF';

    return (
      <div style={{
        borderLeft: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
        background: cellBg,
        opacity: isFuture ? 0.35 : 1,
        padding: '0.5rem 0.5rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        boxSizing: 'border-box',
      }}>
        {confirmed && (
          <div style={{ position:'absolute', top:0, left:0, right:0, height:'2px', background:C.green }} />
        )}

        {!entry ? (
          <>
            {Object.values(ROW_H).map((h, i) => <div key={i} style={{ height:h, width:'100%' }} />)}
            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)' }}>
              <span style={{ fontSize:'0.72rem', fontWeight:700, color: isFuture ? C.border : C.red }}>
                {isFuture ? '—' : 'Missing'}
              </span>
            </div>
          </>
        ) : (
          <>
            {/* ROW 1: Submitter + timestamp */}
            <div style={{ height:ROW_H.submitter, width:'100%', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexDirection:'column', gap:'1px' }}>
              <span style={{ fontSize:'0.6rem', color:C.mid, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {entry.submitted_by || ''}
              </span>
              {entry.submitted_at && (
                <span style={{ fontSize:'0.52rem', color:C.border, whiteSpace:'nowrap' }}>
                  {new Date(entry.submitted_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}
                </span>
              )}
            </div>

            {/* ROW 2: Receipts — always shown */}
            <div style={{ height:ROW_H.receipts, width:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
              {(entry.receipts_total||0) > 0
                ? <span style={{ fontSize:'0.62rem', color:C.charcoal, fontWeight:800 }}>rcpts €{entry.receipts_total.toFixed(0)}</span>
                : <span style={{ fontSize:'0.62rem', color:C.border }}>—</span>
              }
            </div>

            {/* ROW 3: Drop amount */}
            <div style={{ height:ROW_H.drop, width:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:'1.3rem', fontWeight:900, color: confirmed ? C.green : C.charcoal, letterSpacing:'-0.02em' }}>
                €{count.toFixed(0)}
              </span>
            </div>

            {/* ROW 4: Tebi */}
            <div style={{ height:ROW_H.tebi, width:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:'0.75rem', color:C.navy, fontWeight:500 }}>
                €{(entry.tebi_total||0).toFixed(0)}
              </span>
            </div>

            {/* ROW 5: Variance */}
            <div style={{ height:ROW_H.variance, width:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
              {varChip(entry.variance)}
            </div>

            {/* ROW 6: Amended + edit links */}
            <div style={{ height:ROW_H.amended, width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.35rem' }}>
              {entry.amended && (
                <span style={{ fontSize:'0.54rem', fontWeight:800, color:C.amber, textTransform:'uppercase', letterSpacing:'0.04em' }}>
                  Amended
                </span>
              )}
              {isAdmin && (
                <>
                  <button onClick={() => setAmendingEntry({ entry, newCashTotal: String(entry.cash_total || '') })}
                    style={{ fontSize:'0.54rem', color:C.mid, background:'none', border:'none', cursor:'pointer', textDecoration:'underline', padding:0 }}>
                    cash
                  </button>
                  <button onClick={() => setAmendingReceipts({ entry, receipts: (entry.receipts||[]).map((r,i)=>({...r,id:i})) })}
                    style={{ fontSize:'0.54rem', color:C.mid, background:'none', border:'none', cursor:'pointer', textDecoration:'underline', padding:0 }}>
                    rcpts
                  </button>
                </>
              )}
            </div>

            {/* ROW 7: Confirm / confirmed */}
            <div style={{ height:ROW_H.action, width:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
              {confirmed ? (
                <div style={{ display:'flex', alignItems:'center', gap:'0.3rem' }}>
                  <span style={{ fontSize:'0.62rem', fontWeight:700, color:C.green }}>✓ Confirmed</span>
                  {isAdmin && (
                    <button onClick={() => handleUnconfirm(entry)} disabled={confirming===entry.id}
                      style={{ fontSize:'0.57rem', color:C.mid, background:'none', border:'none', cursor:'pointer', textDecoration:'underline', padding:0 }}>
                      undo
                    </button>
                  )}
                </div>
              ) : isAdmin ? (
                <button onClick={() => handleConfirm(entry)} disabled={confirming===entry.id}
                  style={{
                    width:'100%', padding:'0.28rem 0',
                    background: confirming===entry.id ? C.border : C.navy,
                    color:C.white, border:'none', borderRadius:0,
                    fontSize:'0.62rem', fontWeight:700,
                    textTransform:'uppercase', letterSpacing:'0.06em',
                    cursor: confirming===entry.id ? 'default' : 'pointer',
                  }}>
                  {confirming===entry.id ? '…' : 'Confirm'}
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Amend Drop Modal (admin only) ────────────────────────────────────────────
  function AmendDropModal({ state, onClose }) {
    const { entry } = state;
    const [val, setVal] = useState(state.newCashTotal);
    const parsed   = parseFloat(val) || 0;
    const newDrop  = Math.max(0, parsed - FLOAT + (entry.receipts_total || 0));
    const oldDrop  = Math.max(0, (entry.cash_total || 0) - FLOAT + (entry.receipts_total || 0));
    const canSave  = parsed > 0 && parsed !== (entry.cash_total || 0);
    return (
      <div style={{ position:'fixed', inset:0, zIndex:3000, background:'rgba(47,58,66,0.6)',
        display:'flex', alignItems:'center', justifyContent:'center', padding:'1.25rem' }}>
        <div style={{ background:C.white, borderRadius:'18px', width:'100%', maxWidth:'340px',
          padding:'1.5rem', boxShadow:'0 16px 48px rgba(0,0,0,0.25)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <h3 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:C.charcoal }}>Amend Cash Total</h3>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:C.mid }}><X size={16} /></button>
          </div>
          <div style={{ marginBottom:'0.75rem', padding:'0.6rem 0.8rem', background:C.cream, borderRadius:'9px', fontSize:'0.78rem', color:C.mid }}>
            <strong style={{ color:C.charcoal }}>{entry.venue}</strong> · {entry.date}<br />
            Submitted by: {entry.submitted_by || '—'}
          </div>
          <label style={{ display:'block', fontSize:'0.72rem', fontWeight:800, color:C.navy,
            textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.38rem' }}>
            Corrected cash total (counted) <span style={{ color:C.red }}>*</span>
          </label>
          <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.85rem' }}>
            <span style={{ fontSize:'1rem', fontWeight:700, color:C.mid }}>€</span>
            <input type="number" step="0.01" min="0" value={val} onChange={e => setVal(e.target.value)} autoFocus
              style={{ flex:1, padding:'0.5rem 0.65rem', border:`2px solid ${val ? C.navy : C.border}`,
                borderRadius:'10px', fontSize:'1.1rem', fontWeight:700, color:C.charcoal, outline:'none', background:C.white }} />
          </div>
          {parsed > 0 && (
            <div style={{ padding:'0.6rem 0.8rem', background:C.greenLight, borderRadius:'9px', marginBottom:'0.85rem', fontSize:'0.78rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', color:C.mid, marginBottom:'0.2rem' }}>
                <span>Previous drop</span><span>€{oldDrop.toFixed(2)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, color:C.charcoal }}>
                <span>New drop</span><span>€{newDrop.toFixed(2)}</span>
              </div>
              <div style={{ marginTop:'0.35rem', fontSize:'0.7rem', color:C.amber }}>
                ⚠ Will auto-unconfirm — requires re-confirmation
              </div>
            </div>
          )}
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={onClose}
              style={{ flex:1, padding:'0.62rem', border:`1px solid ${C.border}`, borderRadius:'10px',
                background:C.white, color:C.mid, fontSize:'0.88rem', fontWeight:700, cursor:'pointer' }}>
              Cancel
            </button>
            <button onClick={() => handleAmendDrop(entry, parsed)} disabled={!canSave || amendSaving}
              style={{ flex:2, padding:'0.62rem', border:'none', borderRadius:'10px',
                background: canSave ? C.amber : C.border, color:C.white,
                fontSize:'0.88rem', fontWeight:800, cursor: canSave ? 'pointer' : 'not-allowed' }}>
              {amendSaving ? 'Saving…' : 'Save Amendment'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Amend Receipts Modal (admin only) ───────────────────────────────────────
  function AmendReceiptsModal({ state, onClose }) {
    const { entry } = state;
    const [receipts, setReceipts] = useState(state.receipts.length > 0 ? state.receipts : []);
    const total = receipts.reduce((s,r) => s + (parseFloat(r.amount)||0), 0);

    function addRow() { setReceipts(p => [...p, { id: Date.now(), amount:'', description:'' }]); }
    function upd(id, f, v) { setReceipts(p => p.map(r => r.id===id ? {...r,[f]:v} : r)); }
    function del(id) { setReceipts(p => p.filter(r => r.id!==id)); }

    return (
      <div style={{ position:'fixed', inset:0, zIndex:3000, background:'rgba(47,58,66,0.6)',
        display:'flex', alignItems:'center', justifyContent:'center', padding:'1.25rem' }}>
        <div style={{ background:C.white, borderRadius:'18px', width:'100%', maxWidth:'380px',
          padding:'1.5rem', boxShadow:'0 16px 48px rgba(0,0,0,0.25)', maxHeight:'85vh', overflowY:'auto' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <h3 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:C.charcoal }}>Amend Receipts</h3>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:C.mid }}><X size={16} /></button>
          </div>
          <div style={{ marginBottom:'0.75rem', padding:'0.6rem 0.8rem', background:C.cream, borderRadius:'9px', fontSize:'0.78rem', color:C.mid }}>
            <strong style={{ color:C.charcoal }}>{entry.venue}</strong> · {entry.date}<br />
            Submitted by: {entry.submitted_by || '—'}
          </div>
          {receipts.length === 0 && (
            <p style={{ fontSize:'0.8rem', color:C.mid, marginBottom:'0.75rem' }}>No receipts — add one below.</p>
          )}
          {receipts.map(r => (
            <div key={r.id} style={{ display:'flex', gap:'0.4rem', alignItems:'center', marginBottom:'0.45rem' }}>
              <span style={{ fontSize:'0.85rem', color:C.mid }}>€</span>
              <input type="number" step="0.01" min="0" value={r.amount}
                onChange={e => upd(r.id,'amount',e.target.value)}
                placeholder="0.00"
                style={{ width:'70px', padding:'0.35rem 0.4rem', border:`1.5px solid ${r.amount?C.navy:C.border}`,
                  borderRadius:'7px', fontSize:'0.9rem', fontWeight:700, color:C.charcoal, outline:'none', textAlign:'center' }} />
              <input type="text" value={r.description}
                onChange={e => upd(r.id,'description',e.target.value)}
                placeholder="What for?"
                style={{ flex:1, padding:'0.35rem 0.5rem', border:`1.5px solid ${C.border}`,
                  borderRadius:'7px', fontSize:'0.82rem', color:C.charcoal, outline:'none' }} />
              <button onClick={() => del(r.id)} style={{ background:'none', border:'none', cursor:'pointer', color:C.mid, padding:'0.1rem', flexShrink:0 }}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <button onClick={addRow}
            style={{ display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.3rem 0.7rem',
              border:`1px dashed ${C.border}`, borderRadius:'7px', background:'none',
              color:C.mid, fontSize:'0.78rem', cursor:'pointer', marginBottom:'0.85rem', marginTop:'0.1rem' }}>
            <Plus size={11} /> Add receipt
          </button>
          {receipts.length > 0 && (
            <div style={{ padding:'0.5rem 0.75rem', background:C.greenLight, borderRadius:'8px',
              marginBottom:'0.85rem', fontSize:'0.82rem', display:'flex', justifyContent:'space-between' }}>
              <span style={{ color:C.mid }}>New receipts total</span>
              <strong style={{ color:C.charcoal }}>€{total.toFixed(2)}</strong>
            </div>
          )}
          <div style={{ fontSize:'0.7rem', color:C.amber, marginBottom:'0.75rem' }}>
            ⚠ Will auto-unconfirm — requires re-confirmation
          </div>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={onClose}
              style={{ flex:1, padding:'0.62rem', border:`1px solid ${C.border}`, borderRadius:'10px',
                background:C.white, color:C.mid, fontSize:'0.88rem', fontWeight:700, cursor:'pointer' }}>
              Cancel
            </button>
            <button onClick={() => handleAmendReceipts(entry, receipts)} disabled={amendSaving}
              style={{ flex:2, padding:'0.62rem', border:'none', borderRadius:'10px',
                background:C.amber, color:C.white, fontSize:'0.88rem', fontWeight:800,
                cursor: amendSaving ? 'not-allowed' : 'pointer', opacity: amendSaving ? 0.7 : 1 }}>
              {amendSaving ? 'Saving…' : 'Save Receipts'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {amendingEntry    && <AmendDropModal     state={amendingEntry}    onClose={() => setAmendingEntry(null)} />}
      {amendingReceipts && <AmendReceiptsModal state={amendingReceipts} onClose={() => setAmendingReceipts(null)} />}
      <style>{`
        @media (min-width: 768px) {
          .cashup-mobile  { display: none !important; }
          .cashup-desktop { display: block !important; }
        }
        @media (max-width: 767px) {
          .cashup-mobile  { display: flex !important; }
          .cashup-desktop { display: none !important; }
        }
      `}</style>

      {/* Week nav */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.25rem', flexWrap:'wrap', gap:'0.65rem' }}>
        <h3 style={{ margin:0, fontSize:'1.05rem', fontWeight:800, color:C.charcoal, letterSpacing:'-0.01em' }}>Cash Up</h3>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <button onClick={() => { setWeekStart(prevMonday); setOpenVenue(null); }}
            style={{ padding:'0.32rem 0.8rem', border:`1px solid ${C.border}`, borderRadius:0, background:C.white, color:C.navy, fontSize:'0.78rem', fontWeight:600, cursor:'pointer' }}>
            ‹ Prev
          </button>
          <span style={{ fontSize:'0.88rem', fontWeight:700, color:C.charcoal, whiteSpace:'nowrap', padding:'0 0.25rem' }}>
            {wkLabel(weekStart, weekEnd)}
          </span>
          <button onClick={() => { setWeekStart(nextMonday); setOpenVenue(null); }} disabled={isThisWeek}
            style={{ padding:'0.32rem 0.8rem', border:`1px solid ${C.border}`, borderRadius:0, background:isThisWeek ? C.cream : C.white, color:isThisWeek ? C.border : C.navy, fontSize:'0.78rem', fontWeight:600, cursor:isThisWeek ? 'default' : 'pointer' }}>
            Next ›
          </button>
        </div>
      </div>

      {loading ? (
        <p style={{ color:C.mid, fontSize:'0.85rem' }}>Loading…</p>
      ) : (<>

        {/* ── DESKTOP GRID ── */}
        <div className="cashup-desktop" style={{ display:'none', border:`1px solid ${C.border}`, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'130px repeat(7, 1fr)' }}>

            {/* Header row */}
            <div style={{ background:C.navy, padding:'0.6rem 0.75rem', display:'flex', alignItems:'center', borderBottom:`1px solid rgba(255,255,255,0.1)` }}>
              <span style={{ fontSize:'0.6rem', fontWeight:700, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:'0.1em' }}>Venue</span>
            </div>
            {weekDays.map((day, i) => {
              const dateStr  = toStr(day);
              const isToday  = dateStr === today;
              const isFuture = dateStr > today;
              return (
                <div key={dateStr} style={{
                  background: isToday ? '#2B4C7E' : C.navy,
                  borderLeft: '1px solid rgba(255,255,255,0.1)',
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  padding: '0.6rem 0.5rem',
                  textAlign: 'center',
                  opacity: isFuture ? 0.4 : 1,
                }}>
                  <div style={{ fontSize:'0.75rem', fontWeight:800, color: isToday ? '#93C5FD' : C.white }}>
                    {DAY_LABELS[i]}
                    {isToday && <span style={{ marginLeft:'0.35rem', fontSize:'0.6rem', color:'#93C5FD' }}>Today</span>}
                  </div>
                  <div style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.45)', marginTop:'0.1rem' }}>
                    {day.toLocaleDateString('en-GB', { day:'numeric', month:'short' })}
                  </div>
                </div>
              );
            })}

            {/* Venue rows */}
            {VENUES.map((venue, vi) => {
              const id       = VENUE_ID[venue];
              const stats    = venueWeekStats(venue);
              const absAvg   = stats ? Math.abs(stats.avgVariance) : 0;
              const varColor = absAvg<=5 ? C.green : absAvg<=10 ? C.amber : C.red;
              const varBg    = absAvg<=5 ? C.greenLight : absAvg<=10 ? C.amberLight : C.redLight;
              const topBorder = vi > 0 ? `2px solid ${C.border}` : 'none';

              return [
                <div key={`label-${venue}`} style={{
                  borderTop: topBorder,
                  borderBottom: `1px solid ${C.border}`,
                  borderRight: `1px solid ${C.border}`,
                  background: C.skyBlueLight,
                  padding: '0.5rem 0.75rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  boxSizing: 'border-box',
                }}>
                  {/* Venue name + days */}
                  <div style={{ marginBottom:'0.3rem' }}>
                    <div style={{ fontSize:'0.92rem', fontWeight:800, color:C.charcoal }}>{id.short}</div>
                    {stats && (
                      <div style={{ fontSize:'0.62rem', color:C.mid, fontWeight:600 }}>
                        {stats.daysSubmitted}/{stats.daysExpected} days
                      </div>
                    )}
                  </div>

                  {/* ROW 1 mirror: submitter (empty) */}
                  <div style={{ height:ROW_H.submitter }} />

                  {/* ROW 2 mirror: receipts label */}
                  <div style={{ height:ROW_H.receipts, display:'flex', alignItems:'center' }}>
                    <span style={{ fontSize:'0.58rem', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em' }}>Receipts</span>
                  </div>

                  {/* ROW 3 mirror: drop total */}
                  <div style={{ height:ROW_H.drop, display:'flex', alignItems:'center' }}>
                    <div>
                      <div style={{ fontSize:'0.56rem', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', lineHeight:1, marginBottom:'0.15rem' }}>Count</div>
                      <div style={{ fontSize:'1.05rem', fontWeight:800, color:C.charcoal, letterSpacing:'-0.02em', lineHeight:1 }}>
                        {stats ? `€${stats.totalDrop.toFixed(0)}` : '—'}
                      </div>
                    </div>
                  </div>

                  {/* ROW 4 mirror: tebi */}
                  <div style={{ height:ROW_H.tebi, display:'flex', alignItems:'center' }}>
                    <div>
                      <div style={{ fontSize:'0.56rem', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', lineHeight:1, marginBottom:'0.1rem' }}>Tebi</div>
                      <div style={{ fontSize:'0.8rem', fontWeight:700, color:C.navy, lineHeight:1 }}>
                        {stats ? `€${stats.totalTebi.toFixed(0)}` : '—'}
                      </div>
                    </div>
                  </div>

                  {/* ROW 5 mirror: avg variance */}
                  <div style={{ height:ROW_H.variance, display:'flex', alignItems:'center' }}>
                    {stats ? (
                      <span style={{ fontSize:'0.72rem', fontWeight:700, color:varColor, background:varBg, padding:'0.12rem 0.45rem', borderRadius:'999px' }}>
                        {stats.avgVariance>=0?'+':''}€{stats.avgVariance.toFixed(2)}
                      </span>
                    ) : null}
                  </div>

                  {/* ROW 6 + 7: spacers */}
                  <div style={{ height:ROW_H.amended }} />
                  <div style={{ height:ROW_H.action }} />
                </div>,

                ...weekDays.map((day) => (
                  <DayCell key={`${venue}-${toStr(day)}`} venue={venue} day={day} />
                )),
              ];
            })}
          </div>
        </div>

        {/* ── MOBILE ACCORDION ── */}
        <div className="cashup-mobile" style={{ display:'none', flexDirection:'column', gap:'1rem' }}>
          {VENUES.map(venue => {
            const id     = VENUE_ID[venue];
            const stats  = venueWeekStats(venue);
            const isOpen = openVenue === venue;
            const absAvg = stats ? Math.abs(stats.avgVariance) : 0;
            const varColor = absAvg<=5 ? C.green : absAvg<=10 ? C.amber : C.red;
            return (
              <div key={venue} style={{ background:C.white, borderRadius:'14px', border:`1px solid ${isOpen ? id.border : C.border}`, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                <div onClick={() => setOpenVenue(isOpen ? null : venue)}
                  style={{ display:'flex', alignItems:'center', padding:'1rem 1.25rem', cursor:'pointer', gap:'1rem', background:isOpen ? id.bg : C.white }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'0.75rem', flexWrap:'wrap' }}>
                      <span style={{ fontSize:'1rem', fontWeight:800, color:id.text }}>{id.short}</span>
                      {stats ? (
                        <>
                          <span style={{ fontSize:'0.75rem', color:C.mid }}>{stats.daysSubmitted}/{stats.daysExpected} days</span>
                          {varChip(stats.avgVariance)}
                        </>
                      ) : (
                        <span style={{ fontSize:'0.75rem', color:C.mid }}>No entries this week</span>
                      )}
                    </div>
                    {stats && (
                      <div style={{ display:'flex', gap:'1.25rem', flexWrap:'wrap' }}>
                        {[
                          { label:'Total Count', value:`€${stats.totalDrop.toFixed(0)}` },
                          { label:'Total Tebi',  value:`€${stats.totalTebi.toFixed(0)}` },
                          { label:'Avg Variance', variance: stats.avgVariance },
                        ].map(({ label, value, variance }) => (
                          <div key={label}>
                            <div style={{ fontSize:'0.6rem', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'2px' }}>{label}</div>
                            {variance !== undefined
                              ? <div style={{ fontSize:'1rem', fontWeight:800, color:varColor }}>{variance>=0?'+':'-'}€{Math.abs(variance).toFixed(2)}</div>
                              : <div style={{ fontSize:'1rem', fontWeight:800, color:C.charcoal }}>{value}</div>
                            }
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronDown size={16} color={C.mid} style={{ transform:isOpen?'rotate(180deg)':'none', transition:'transform 0.2s', flexShrink:0 }} />
                </div>
                {isOpen && (
                  <div style={{ borderTop:`1px solid ${C.border}` }}>
                    {weekDays.map((day, i) => {
                      const dateStr  = toStr(day);
                      const entry    = getEntry(venue, dateStr);
                      const isToday  = dateStr === today;
                      const isFuture = dateStr > today;
                      const count    = entry ? Math.max(0,(entry.cash_total||0) - FLOAT - (entry.receipts_total||0)) : null;
                      return (
                        <div key={dateStr} style={{ padding:'0.85rem 1.25rem', borderBottom:i<6?`1px solid ${C.border}`:'none', background:isToday?'#F8FAFC':C.white, opacity:isFuture?0.45:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
                            <div style={{ minWidth:'72px' }}>
                              <div style={{ fontSize:'0.78rem', fontWeight:isToday?800:600, color:isToday?id.text:C.charcoal }}>
                                {DAY_LABELS[i]}{isToday && <span style={{ marginLeft:'0.3rem', fontSize:'0.62rem', color:id.text }}>Today</span>}
                              </div>
                              <div style={{ fontSize:'0.68rem', color:C.mid }}>{day.toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</div>
                            </div>
                            {!entry ? (
                              <span style={{ fontSize:'0.78rem', color:isFuture?C.border:C.red, fontWeight:500 }}>{isFuture?'—':'Not submitted'}</span>
                            ) : (
                              <div style={{ display:'flex', alignItems:'center', gap:'1rem', flex:1, flexWrap:'wrap' }}>
                                {[{label:'Count',value:`€${count.toFixed(0)}`},{label:'Tebi',value:`€${(entry.tebi_total||0).toFixed(0)}`}].map(({label,value})=>(
                                  <div key={label} style={{ textAlign:'center' }}>
                                    <div style={{ fontSize:'0.6rem', color:C.mid, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</div>
                                    <div style={{ fontSize:'0.9rem', fontWeight:800, color:C.charcoal }}>{value}</div>
                                  </div>
                                ))}
                                <div style={{ textAlign:'center' }}>
                                  <div style={{ fontSize:'0.6rem', color:C.mid, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em' }}>Variance</div>
                                  {varChip(entry.variance)}
                                </div>
                                {(entry.receipts_total||0)>0 && (
                                  <div style={{ textAlign:'center' }}>
                                    <div style={{ fontSize:'0.6rem', color:C.mid, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em' }}>Receipts</div>
                                    <div style={{ fontSize:'0.85rem', fontWeight:800, color:C.charcoal }}>€{entry.receipts_total.toFixed(2)}</div>
                                  </div>
                                )}
                                <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
                                  {entry.submitted_by && (
                                    <div style={{ textAlign:'right' }}>
                                      <span style={{ fontSize:'0.68rem', color:C.mid, display:'block' }}>{entry.submitted_by}</span>
                                      {entry.submitted_at && (
                                        <span style={{ fontSize:'0.6rem', color:C.border, display:'block' }}>
                                          {new Date(entry.submitted_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {entry.amended && <span style={{ fontSize:'0.65rem', fontWeight:700, color:C.amber, background:C.amberLight, padding:'0.1rem 0.4rem', borderRadius:'999px' }}>Amended</span>}
                                  {entry.confirmed ? (
                                    <div style={{ display:'flex', alignItems:'center', gap:'0.35rem' }}>
                                      <span style={{ fontSize:'0.68rem', color:C.green, fontWeight:700 }}>✓ Confirmed</span>
                                      {isAdmin && <button onClick={()=>handleUnconfirm(entry)} disabled={confirming===entry.id} style={{ fontSize:'0.65rem', color:C.mid, background:'none', border:'none', cursor:'pointer', textDecoration:'underline', padding:0 }}>undo</button>}
                                    </div>
                                  ) : isAdmin ? (
                                    <button onClick={()=>handleConfirm(entry)} disabled={confirming===entry.id}
                                      style={{ padding:'0.28rem 0.75rem', background:C.navy, color:C.white, border:'none', borderRadius:'7px', fontSize:'0.72rem', fontWeight:700, cursor:confirming===entry.id?'default':'pointer', opacity:confirming===entry.id?0.6:1 }}>
                                      {confirming===entry.id?'…':'Confirm count'}
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            )}
                          </div>
                          {entry?.notes && (
                            <div style={{ marginTop:'0.5rem', padding:'0.4rem 0.65rem', background:C.cream, borderRadius:'7px', fontSize:'0.75rem', color:C.charcoal }}>
                              <span style={{ fontWeight:600, color:C.mid }}>Note: </span>{entry.notes}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </>)}
    </div>
  );
}
// ─── CASH REPORT TAB (annual view — admin only) ───────────────────────────────
const MONTH_NAMES   = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const REPORT_VENUES = ['Box Jordaan','Box Plantage','Sassys'];

const REPORT_VENUE_ID = {
  'Box Jordaan':  { bg:'#EDF3FC', text:'#2563EB', border:'#BAD4F5', accent:'#2563EB' },
  'Box Plantage': { bg:'#C8E6D8', text:'#2D7A65', border:'#7EA594', accent:'#2D7A65' },
  'Sassys':       { bg:'#EDE0F7', text:'#6D28A0', border:'#C4A8E8', accent:'#6D28A0' },
};

// ── Canvas PNG generator ──────────────────────────────────────────────────────
function downloadMonthPNG({ venue, monthName, year, data }) {
  const { confirmedCash, totalTebi, totalReceipts, totalVariance, receiptLines, daysConfirmed, daysSubmitted } = data;
  const vid      = REPORT_VENUE_ID[venue];
  const hasKassa = totalVariance < -0.01;
  const DPR      = 2; // retina

  // Layout constants
  const W       = 520;
  const PAD     = 32;
  const ROW_H   = 52;
  const ROWS    = 4; // Tebi / Confirmed / Receipts / Variance
  const RECEIPT_ROW_H = 28;
  const NOTE_H  = hasKassa ? 56 : 0;
  const FOOTER_H = 40;
  const HEADER_H = 80;
  const receiptBlockH = receiptLines.length > 0 ? 24 + receiptLines.length * RECEIPT_ROW_H + 8 : 0;
  const H = HEADER_H + ROWS * ROW_H + 1 + receiptBlockH + NOTE_H + FOOTER_H + PAD;

  const canvas  = document.createElement('canvas');
  canvas.width  = W * DPR;
  canvas.height = H * DPR;
  const ctx     = canvas.getContext('2d');
  ctx.scale(DPR, DPR);

  // helpers
  function rect(x, y, w, h, color) { ctx.fillStyle = color; ctx.fillRect(x, y, w, h); }
  function roundRect(x, y, w, h, r, color) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath(); ctx.fill();
  }
  function text(str, x, y, { size=13, weight='normal', color='#2F3A42', align='left' } = {}) {
    ctx.font = `${weight} ${size}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = align;
    ctx.fillText(str, x, y);
  }
  function line(x1, y1, x2, y2, color='#D8DEE3', width=1) {
    ctx.strokeStyle = color; ctx.lineWidth = width;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  }

  // Background
  roundRect(0, 0, W, H, 12, '#FFFFFF');

  // Header band
  roundRect(0, 0, W, HEADER_H, 12, vid.accent);
  rect(0, HEADER_H - 12, W, 12, vid.accent); // fill bottom corners
  text(venue, PAD, 32, { size:17, weight:'700', color:'#FFFFFF' });
  text(`Cash Report — ${monthName} ${year}`, PAD, 54, { size:12, color:'rgba(255,255,255,0.8)' });
  text(`${daysConfirmed}/${daysSubmitted} days confirmed`, W - PAD, 54, { size:11, color:'rgba(255,255,255,0.65)', align:'right' });

  // Metric rows
  const METRICS = [
    { label: 'Tebi Cash (POS)',    value: totalTebi,       bold: false },
    { label: 'Actual Cash Drop',   value: confirmedCash,   bold: true  },
    { label: 'Receipts Total',     value: totalReceipts,   bold: false },
    { label: 'Variance',           value: totalVariance,   isVariance: true },
  ];

  let y = HEADER_H;
  METRICS.forEach((m, i) => {
    const bg = i % 2 === 0 ? '#FAFAF8' : '#FFFFFF';
    rect(0, y, W, ROW_H, bg);
    if (i > 0) line(0, y, W, y);

    text(m.label, PAD, y + ROW_H / 2 + 5, { size:13, weight: m.bold ? '700' : '400', color:'#7A8A96' });

    if (m.isVariance) {
      const abs = Math.abs(totalVariance);
      const vColor = abs <= 5 ? '#7EA594' : abs <= 10 ? '#E07B00' : '#DC3545';
      const vBg    = abs <= 5 ? '#EAF2EF' : abs <= 10 ? '#FFF3E0' : '#FFF0F0';
      const vStr   = `${totalVariance >= 0 ? '+' : ''}€${totalVariance.toFixed(2)}`;
      const pill_w = 90; const pill_h = 26; const pill_x = W - PAD - pill_w; const pill_y = y + (ROW_H - pill_h) / 2;
      roundRect(pill_x, pill_y, pill_w, pill_h, 13, vBg);
      text(vStr, pill_x + pill_w / 2, pill_y + 17, { size:13, weight:'700', color:vColor, align:'center' });
    } else {
      const val = m.value > 0 ? `€${m.value.toLocaleString('nl-NL', { minimumFractionDigits:2, maximumFractionDigits:2 })}` : '—';
      const col = m.value > 0 ? (m.bold ? '#2F3A42' : '#445366') : '#D8DEE3';
      text(val, W - PAD, y + ROW_H / 2 + 5, { size:14, weight: m.bold ? '800' : '500', color:col, align:'right' });
    }
    y += ROW_H;
  });

  line(0, y, W, y, '#D8DEE3', 1);
  y += 1;

  // Receipts block
  if (receiptLines.length > 0) {
    rect(0, y, W, receiptBlockH, '#FDFCF8');
    text('RECEIPTS', PAD, y + 18, { size:10, weight:'700', color:'#7A8A96' });
    let ry = y + 24;
    receiptLines.forEach((r, ri) => {
      if (ri > 0) line(PAD, ry, W - PAD, ry, '#D8DEE333', 1);
      text(r.date, PAD, ry + 18, { size:11, color:'#B0BAC3' });
      text(r.description || '(no description)', PAD + 72, ry + 18, { size:11, weight:'600', color:'#2F3A42' });
      text(`€${parseFloat(r.amount).toFixed(2)}`, W - PAD, ry + 18, { size:11, weight:'700', color:'#2F3A42', align:'right' });
      ry += RECEIPT_ROW_H;
    });
    y += receiptBlockH;
    line(0, y, W, y);
  }

  // Kassaverschil note
  if (hasKassa) {
    rect(0, y, W, NOTE_H, '#FFF0F0');
    text(`⚠  Book kassaverschil of €${Math.abs(totalVariance).toFixed(2)} as cash shrinkage expense`, PAD, y + 22, { size:12, weight:'600', color:'#DC3545' });
    text('Do not reduce revenue — upload receipts to Zenvoices', PAD, y + 40, { size:11, color:'#DC3545' });
    y += NOTE_H;
  }

  // Footer
  rect(0, y, W, FOOTER_H + 12, '#F7F6EF');
  text('Receipts uploaded to Zenvoices', PAD, y + 18, { size:11, color:'#7A8A96' });
  text(`Generated ${new Date().toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}`, W - PAD, y + 18, { size:11, color:'#B0BAC3', align:'right' });
  text('Lekker Mate B.V.', PAD, y + 34, { size:10, weight:'600', color:'#B0BAC3' });

  // Border
  ctx.strokeStyle = '#D8DEE3'; ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, W - 1, H - 1);

  // Download
  const slug    = venue.toLowerCase().replace(/\s+/g,'-');
  const mnSlug  = monthName.toLowerCase();
  const link    = document.createElement('a');
  link.download = `cash-report-${slug}-${mnSlug}-${year}.png`;
  link.href     = canvas.toDataURL('image/png');
  link.click();
}

export function CashReportTab() {
  const thisYear = new Date().getFullYear();
  const [year,    setYear]    = useState(thisYear);
  const [entries, setEntries] = useState({});   // keyed by venue
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // 'Venue-MonthIdx'

  // Load all three venues for the year in parallel
  useEffect(() => {
    setLoading(true);
    const from = `${year}-01-01`;
    const to   = `${year}-12-31`;
    Promise.all(REPORT_VENUES.map(v =>
      supabase.from('cashup_log').select('*')
        .eq('venue', v).gte('date', from).lte('date', to)
        .order('date', { ascending: true })
        .then(({ data }) => [v, data || []])
    )).then(results => {
      setEntries(Object.fromEntries(results));
      setLoading(false);
    });
  }, [year]);

  function getMonthData(venueRows, mi) {
    const rows = venueRows.filter(e => parseInt(e.date.split('-')[1], 10) === mi + 1);
    if (!rows.length) return null;
    const confirmedRows = rows.filter(e => e.confirmed);
    return {
      confirmedCash:  confirmedRows.reduce((s,e) => s + Math.max(0,(e.cash_total||0) - FLOAT + (e.receipts_total||0)), 0),
      totalTebi:      rows.reduce((s,e) => s + (e.tebi_total||0), 0),
      totalReceipts:  rows.reduce((s,e) => s + (e.receipts_total||0), 0),
      totalVariance:  rows.reduce((s,e) => s + (e.variance||0), 0),
      receiptLines:   rows.flatMap(e => (e.receipts||[]).map(r => ({ date: e.date, ...r }))),
      daysSubmitted:  rows.length,
      daysConfirmed:  confirmedRows.length,
    };
  }

  function fmt(n)    { return n != null ? `€${Math.round(n).toLocaleString('nl-NL')}` : '—'; }
  function fmtV(n)   { return n != null ? `${n >= 0 ? '+' : ''}€${n.toFixed(2)}` : '—'; }
  function vColor(v) { if (!v) return C.mid; const a = Math.abs(v); return a<=5?C.green:a<=10?C.amber:C.red; }
  function vBg(v)    { if (!v) return C.cream; const a = Math.abs(v); return a<=5?C.greenLight:a<=10?C.amberLight:C.redLight; }

  const METRICS = [
    { key:'totalTebi',     label:'Tebi Cash'       },
    { key:'confirmedCash', label:'Actual Cash Drop' },
    { key:'totalReceipts', label:'Receipts Total'   },
    { key:'totalVariance', label:'Variance',         isVariance:true },
  ];

  function handleDownload(venue, mi) {
    const key = `${venue}-${mi}`;
    setDownloading(key);
    const data = getMonthData(entries[venue] || [], mi);
    if (!data) { setDownloading(null); return; }
    setTimeout(() => {
      downloadMonthPNG({ venue, monthName: MONTH_NAMES[mi], year, data });
      setDownloading(null);
    }, 50);
  }

  // Column width for each month
  const COL = 68;
  const LABEL_W = 130;
  const TOTAL_COL = 76;

  return (
    <div style={{ padding:'1.25rem', maxWidth:'1100px', margin:'0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem', marginBottom:'1.25rem' }}>
        <div>
          <h2 style={{ margin:'0 0 0.1rem', fontSize:'1.15rem', fontWeight:800, color:C.charcoal }}>Cash Report</h2>
          <p style={{ margin:0, fontSize:'0.78rem', color:C.mid }}>Annual overview — click any month header to download PNG for accountants</p>
        </div>
        <div style={{ display:'flex', gap:'0.35rem', alignItems:'center' }}>
          <button onClick={() => setYear(y => y-1)}
            style={{ padding:'0.32rem 0.75rem', border:`1px solid ${C.border}`, borderRadius:'8px', background:C.white, color:C.navy, fontSize:'0.82rem', fontWeight:700, cursor:'pointer' }}>‹</button>
          <span style={{ fontSize:'0.95rem', fontWeight:800, color:C.charcoal, minWidth:'44px', textAlign:'center' }}>{year}</span>
          <button onClick={() => setYear(y => y+1)} disabled={year >= thisYear}
            style={{ padding:'0.32rem 0.75rem', border:`1px solid ${C.border}`, borderRadius:'8px', background:year>=thisYear?C.cream:C.white, color:year>=thisYear?C.border:C.navy, fontSize:'0.82rem', fontWeight:700, cursor:year>=thisYear?'default':'pointer' }}>›</button>
        </div>
      </div>

      {loading ? <p style={{ color:C.mid, fontSize:'0.85rem' }}>Loading…</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
          {REPORT_VENUES.map(venue => {
            const vid      = REPORT_VENUE_ID[venue];
            const venueRows = entries[venue] || [];

            // Year totals for this venue
            const yearTotals = METRICS.reduce((acc, m) => {
              let sum = 0;
              for (let mi = 0; mi < 12; mi++) {
                const d = getMonthData(venueRows, mi);
                if (d) sum += d[m.key] || 0;
              }
              return { ...acc, [m.key]: sum };
            }, {});

            return (
              <div key={venue} style={{ background:C.white, borderRadius:'14px', border:`1.5px solid ${vid.border}`, overflow:'hidden' }}>
                {/* Venue header */}
                <div style={{ background:vid.bg, padding:'0.7rem 1rem', borderBottom:`1px solid ${vid.border}` }}>
                  <span style={{ fontSize:'0.95rem', fontWeight:800, color:vid.text }}>{venue}</span>
                </div>

                {/* Scrollable table */}
                <div style={{ overflowX:'auto' }}>
                  <table style={{ borderCollapse:'collapse', width:'100%', minWidth: LABEL_W + 12*COL + TOTAL_COL + 'px' }}>
                    <thead>
                      <tr style={{ background:C.navy }}>
                        <th style={{ width:LABEL_W, padding:'0.5rem 0.75rem', textAlign:'left', fontSize:'0.6rem', fontWeight:700, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:'0.07em' }}>Metric</th>
                        {MONTH_SHORT.map((mn, mi) => {
                          const d   = getMonthData(venueRows, mi);
                          const key = `${venue}-${mi}`;
                          const isDl = downloading === key;
                          return (
                            <th key={mn} style={{ width:COL, padding:'0.4rem 0.25rem', textAlign:'center' }}>
                              <button
                                onClick={() => d && handleDownload(venue, mi)}
                                disabled={!d || isDl}
                                title={d ? `Download ${MONTH_NAMES[mi]} report` : 'No data'}
                                style={{ background:'none', border:'none', cursor: d ? 'pointer' : 'default', padding:'0.18rem 0.4rem', borderRadius:'5px',
                                  color: d ? (isDl ? C.green : 'rgba(255,255,255,0.85)') : 'rgba(255,255,255,0.25)',
                                  fontSize:'0.72rem', fontWeight:700, transition:'all 0.12s',
                                  textDecoration: d ? 'underline' : 'none', textDecorationStyle:'dotted' }}>
                                {isDl ? '↓' : mn}
                              </button>
                            </th>
                          );
                        })}
                        <th style={{ width:TOTAL_COL, padding:'0.5rem 0.5rem', textAlign:'right', fontSize:'0.6rem', fontWeight:700, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:'0.07em' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {METRICS.map((metric, ri) => {
                        const isLast = ri === METRICS.length - 1;
                        const rowBg  = ri % 2 === 0 ? C.white : '#FAFAF8';
                        return (
                          <tr key={metric.key} style={{ background:rowBg, borderBottom: isLast ? 'none' : `1px solid ${C.border}` }}>
                            <td style={{ padding:'0.6rem 0.75rem', fontSize:'0.78rem', fontWeight:600, color:C.mid, whiteSpace:'nowrap' }}>
                              {metric.label}
                            </td>
                            {Array.from({length:12}, (_,mi) => {
                              const d = getMonthData(venueRows, mi);
                              const val = d ? d[metric.key] : null;
                              if (!d) return <td key={mi} style={{ textAlign:'center', fontSize:'0.75rem', color:C.border, padding:'0.6rem 0.25rem' }}>—</td>;
                              if (metric.isVariance) {
                                const vc = vColor(val); const vb = vBg(val);
                                return (
                                  <td key={mi} style={{ textAlign:'center', padding:'0.5rem 0.2rem' }}>
                                    <span style={{ fontSize:'0.68rem', fontWeight:700, color:vc, background:vb, padding:'0.12rem 0.4rem', borderRadius:'999px', display:'inline-block', whiteSpace:'nowrap' }}>
                                      {fmtV(val)}
                                    </span>
                                  </td>
                                );
                              }
                              const showReceipts = metric.key === 'totalReceipts';
                              return (
                                <td key={mi} style={{ textAlign:'right', padding:'0.6rem 0.4rem 0.6rem 0.2rem', fontSize:'0.78rem', fontWeight: metric.key==='confirmedCash'?800:500, color: val>0?C.charcoal:C.border, whiteSpace:'nowrap' }}>
                                  {val > 0 ? fmt(val) : (showReceipts ? '—' : fmt(val))}
                                </td>
                              );
                            })}
                            {/* Year total */}
                            <td style={{ textAlign:'right', padding:'0.6rem 0.75rem', fontSize:'0.8rem', fontWeight:800, background: '#F0F4F8', borderLeft:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>
                              {metric.isVariance ? (
                                <span style={{ color:vColor(yearTotals[metric.key]), fontWeight:800 }}>{fmtV(yearTotals[metric.key])}</span>
                              ) : (
                                <span style={{ color:C.charcoal }}>{fmt(yearTotals[metric.key])}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Accountant note */}
      <div style={{ marginTop:'1rem', padding:'0.75rem 1rem', background:C.amberLight, borderRadius:'10px', fontSize:'0.78rem', color:C.amber, lineHeight:1.6 }}>
        <strong>For accountants:</strong> Receipts are uploaded to Zenvoices. If variance is negative (cash short), book as <em>kassaverschil</em> (cash shrinkage expense). Do not reduce revenue.
      </div>
    </div>
  );
}